'use client';

import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/redux/Store';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'react-toastify';
import { FaSync, FaExclamationTriangle, FaCheckCircle, FaPlus } from 'react-icons/fa';
import './AccessTransactions.scss';

// Mirrors AdminAccessTransactionController's response shape (#57).
interface AccessComparison {
  txHash: string;
  timestamp: number;
  sender: string;
  function: string;
  accessType: 'view' | 'subscription';
  onChainProfileId: number | null;
  amount: string;
  status: string;
  inDatabase: boolean;
  dbRecord?: {
    accessType: string;
    profileId: number;
    userWallet: string;
    paymentAmount: string;
    createdAt: string;
  };
  issue?: string;
}

interface AccessTransactionsData {
  totalContractTransactions: number;
  totalDBRecords: number;
  missingInDB: number;
  comparisons: AccessComparison[];
  pagination: { limit: number; offset: number; total: number };
}

const formatAmountKLV = (smallest: string) => {
  // Payment amounts come back from the parser in smallest units (KLV
  // is 6 decimals after Phase B). Convert for display.
  const n = parseFloat(smallest) / 1_000_000;
  return `${n.toFixed(2)} KLV`;
};

const formatDate = (ts: number) => new Date(ts * 1000).toLocaleString();
const shortHash = (h: string) => `${h.slice(0, 8)}…${h.slice(-8)}`;
const shortWallet = (w: string) => (w.length > 14 ? `${w.slice(0, 8)}…${w.slice(-4)}` : w);

const AccessTransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);

  const [data, setData] = useState<AccessTransactionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get<{ success: boolean; data: AccessTransactionsData }>(
        '/admin/access-transactions',
      );
      if (res.success) setData(res.data);
    } catch (err: any) {
      console.error('Error fetching access transactions:', err);
      toast.error('Failed to load access transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleRecord = async (txHash: string) => {
    setRecording(txHash);
    try {
      const { data: res } = await api.post<{ success?: boolean; alreadyRecorded?: boolean }>(
        '/admin/access-transactions/record',
        { txHash },
      );
      if (res.alreadyRecorded) {
        toast.info('Already recorded — refreshing');
      } else {
        toast.success('Backfilled');
      }
      await fetchData();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to backfill';
      toast.error(msg);
    } finally {
      setRecording(null);
    }
  };

  if (loading) {
    return (
      <div className="access-transactions-page">
        <div className="loading">Loading access transactions…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="access-transactions-page">
        <div className="error">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="access-transactions-page">
      <div className="page-header">
        <h1>Access Transactions Reconcile</h1>
        <p>
          Cross-references on-chain access purchases (<code>purchaseViewAccess</code>,{' '}
          <code>subscribe</code>) against <code>app.access_transaction</code>. Use the Backfill
          button to write the missing row for a paid-but-not-persisted tx.
        </p>
        <button className="btn-refresh" onClick={fetchData} disabled={loading}>
          <FaSync className={loading ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon blockchain"><FaCheckCircle /></div>
          <div className="card-content">
            <h3>Contract Txs</h3>
            <div className="card-value">{data.totalContractTransactions}</div>
            <p>Access purchases on-chain</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon database"><FaCheckCircle /></div>
          <div className="card-content">
            <h3>Database Rows</h3>
            <div className="card-value">{data.totalDBRecords}</div>
            <p>app.access_transaction total</p>
          </div>
        </div>
        <div className={`summary-card ${data.missingInDB > 0 ? 'warning' : ''}`}>
          <div className="card-icon warning"><FaExclamationTriangle /></div>
          <div className="card-content">
            <h3>Missing</h3>
            <div className="card-value">{data.missingInDB}</div>
            <p>Paid on-chain, not recorded</p>
          </div>
        </div>
      </div>

      <div className="transactions-section">
        <h2>Transactions ({data.comparisons.length})</h2>
        <div className="table-container">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>TX</th>
                <th>When</th>
                <th>Type</th>
                <th>Profile</th>
                <th>Sender</th>
                <th>Amount</th>
                <th>Status</th>
                <th>In DB</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.comparisons.map((tx) => (
                <tr key={tx.txHash} className={tx.issue ? 'has-issue' : ''}>
                  <td>
                    <a
                      href={`https://testnet.kleverscan.org/transaction/${tx.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tx-hash"
                      title={tx.txHash}
                    >
                      {shortHash(tx.txHash)}
                    </a>
                  </td>
                  <td>{formatDate(tx.timestamp)}</td>
                  <td>
                    <span className={`type-badge type-${tx.accessType}`}>{tx.accessType}</span>
                  </td>
                  <td>{tx.onChainProfileId ?? '—'}</td>
                  <td title={tx.sender}>{shortWallet(tx.sender)}</td>
                  <td>{formatAmountKLV(tx.amount)}</td>
                  <td>
                    <span className={`status-badge ${tx.status}`}>{tx.status}</span>
                  </td>
                  <td>
                    {tx.inDatabase ? (
                      <span className="badge success"><FaCheckCircle /> Yes</span>
                    ) : (
                      <span className="badge error"><FaExclamationTriangle /> No</span>
                    )}
                  </td>
                  <td>
                    {!tx.inDatabase && tx.status === 'success' && (
                      <button
                        className="btn-record"
                        onClick={() => handleRecord(tx.txHash)}
                        disabled={recording === tx.txHash}
                      >
                        <FaPlus /> {recording === tx.txHash ? 'Recording…' : 'Backfill'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {data.comparisons.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-row">No access-purchase contract events yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccessTransactionsPage;
